/**
 * Public barrel for twilio-diag module.
 *
 * Call initTwilioDiag() once (e.g. in a top-level useEffect in ConsultationScreen)
 * to install window.__twilioDiag when the debug toggle is active.
 * When the toggle is OFF, initTwilioDiag() is a no-op and window.__twilioDiag
 * remains undefined.
 */

export { attachRoomLogger, isDiagEnabled, snapshot, resetBuffer } from './logger';
import { isDiagEnabled, snapshot, log } from './logger';
import type { LogEntry } from './types';

const DIAG_VERSION = '1.0.0';

declare global {
  interface Window {
    __twilioDiag?: {
      dump: () => LogEntry[];
      version: string;
    };
  }
}

function dump(): LogEntry[] {
  const entries = snapshot();

  // Quick scan in DevTools
  console.table(entries.map(e => ({ tag: e.tag, t: e.payload.t })));

  // Copy to clipboard (best-effort — may fail in non-secure contexts or when permission denied)
  const json = JSON.stringify(entries, null, 2);
  try {
    navigator.clipboard.writeText(json).catch(() => {
      // Promise rejected (permission denied, non-secure context, etc.) — fall back to console
      console.log(json);
    });
  } catch {
    // navigator.clipboard itself threw synchronously
    console.log(json);
  }

  return entries;
}

/**
 * Installs window.__twilioDiag = { dump, version } when the debug toggle is ON.
 * Idempotent — safe to call repeatedly; second call is a no-op if already installed.
 * When the toggle is OFF, this function does nothing and the global remains undefined.
 */
export function initTwilioDiag(): void {
  if (!isDiagEnabled()) return;

  // Idempotency guard
  if (typeof window !== 'undefined' && window.__twilioDiag) return;

  if (typeof window !== 'undefined') {
    window.__twilioDiag = { dump, version: DIAG_VERSION };
    log('[twilio:diag:init]', { t: Date.now(), version: DIAG_VERSION });
  }
}
